import {
  Accordion,
  Box,
  Card,
  Container,
  Divider,
  Grid,
  LoadingOverlay,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { IconBook, IconHelp, IconLifebuoy, IconPhone } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'

import { useFetch } from '@/lib/hooks'
import { getUrlString } from '@/lib/utils'
import { Site as SiteType } from '@/types'

interface Props {
  initialData?: {
    site: SiteType
  }
}

export default function HelpView({ initialData }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)
  const theme = useMantineTheme()

  // Fetch
  const { data: siteData, error } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site
  })
  const { data: faqData } = useFetch([siteSlug ? `/${siteSlug}/kb/faq/` : null])

  const isLoading = !siteData && !error

  if (isLoading) {
    return <LoadingOverlay visible />
  }

  return (
    <Container px="xs" py="lg" size="xl">
      <Title mb="lg">Central de Ajuda</Title>

      <Grid sx={{ textAlign: 'center' }} mb="xl">
        <Grid.Col md={4}>
          <Card
            p="xl"
            component={Link}
            href="/help/contacts"
            withBorder
            sx={{ height: '100%', ':hover': { backgroundColor: theme.colors.gray[0] } }}>
            <IconPhone size={48} stroke={1.5} />
            <Title order={2}>Fale Conosco</Title>
            <Text color="dimmed">Consulte os nossos canais de atendimento.</Text>
          </Card>
        </Grid.Col>
        <Grid.Col md={4}>
          <Card
            p="xl"
            component={Link}
            href="/help/articles"
            withBorder
            sx={{ height: '100%', ':hover': { backgroundColor: theme.colors.gray[0] } }}>
            <IconBook size={48} stroke={1.5} />
            <Title order={2}>Tópicos de Ajuda</Title>
            <Text color="dimmed">Acesse os artigos da nossa base de conhecimentos.</Text>
          </Card>
        </Grid.Col>
        <Grid.Col md={4}>
          <Card
            p="xl"
            component={Link}
            href="/help/tickets"
            withBorder
            sx={{ height: '100%', ':hover': { backgroundColor: theme.colors.gray[0] } }}>
            <IconLifebuoy size={48} stroke={1.5} />
            <Title order={2}>Atendimento</Title>
            <Text color="dimmed">
              Está com algum problema? Abra um atendimento que vamos te ajudar.
            </Text>
          </Card>
        </Grid.Col>
      </Grid>

      {Array.isArray(faqData) && faqData?.length > 0 && (
        <>
          <Divider
            my="xl"
            variant="dashed"
            labelPosition="center"
            label={
              <>
                <IconHelp size={32} stroke={1.5} />
                <Title order={2} ml={5}>
                  Dúvidas Frequentes
                </Title>
              </>
            }
          />

          <Accordion variant="contained">
            {faqData?.map((question: any) => (
              <Accordion.Item key={question.uid} value={question.uid}>
                <Accordion.Control>{question.question}</Accordion.Control>
                <Accordion.Panel>
                  <Box dangerouslySetInnerHTML={{ __html: question.answer }} />
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </>
      )}
    </Container>
  )
}
